<i18n>
{
    "uk": {
        "about": {
            "title": "Про курс"
        },

        "stats": {
            "students": "немає студентів | студент | студента | студентів"
        },

        "actions": {
            "program": "Програма курсу",
            "preregister": "Зареєструватись на курс",
            "trial": "Спробувати 7 днів безплатно",
            "buy": "Придбати курс",
            "subscribe": "Оформити передплату",
            "join": "Почати навчання",
            "toCourse": "До курсу"
        }
    },
    "en": {
        "about": {
            "title": "About course"
        },

        "stats": {
            "students": "no students | student | students"
        },

        "actions": {
            "program": "Course program",
            "preregister": "Register for the course",
            "trial": "Try 7 days free",
            "buy": "Buy course",
            "subscribe": "Subscribe",
            "join": "Join course",
            "toCourse": "To course"
        }
    }
}
</i18n>


<template>
    <div class="hero-block-wrapper hero-2_1">
        <div
            :id="block.id || `${block.type}-${uid}`"
            class="hero-block"
            :class="`${heroVisual.dark && 'dark' || ''} ${heroVisual.btnLight ? 'hero-btn-light' : ''} ${block.class || ''}`"
            ref="hero"
            :style="{
                '--hero-cell-height': `${contentHeight}px`,
                '--hero-btn-hue':     heroVisual.btnHue,
                '--hero-btn-sat':     `${heroVisual.btnSaturation}%`,
                '--hero-btn-light':   `${heroVisual.btnLightness}%`,
            }"
        >

            <!-- Edit button -->
            <div
                v-if="edit"
                class="edit"
            >
                <button
                    class="btn-bg btn-lg rounded icon-ic-pencil-outline elevate edit-btn"
                    @click="$emit('openHero2Editor')"
                />
            </div>


            <!-- Background visual -->
            <div
                class="hero-visual"
                :style="{
                    'background-color': heroVisual.color
                }"
            >

                <!-- Video -->
                <template v-if="heroVisual.video">
                    <video
                        v-if="heroVisual.video"
                        class="visual-video"
                        :autoplay="true"
                        :loop="true"
                        :muted="true"
                        :playsinline="true"
                        :poster="heroVisual.image || null"
                        :width="heroVisual.width || null"
                        :height="heroVisual.height || null"
                    >
                        <source
                            v-for="(source, i) in heroVisual.video"
                            :key="`visual-source-${i}`"
                            :src="source.src"
                            :type="source.type"
                        />
                    </video>
                    <div v-if="heroVisual.mask" class="mask" />
                </template>

                <!-- Image -->
                <div class="page-container">
                    <div
                        class="visual"
                        :class="{
                            'mask': heroVisual.mask || !heroVisual.image,
                            'fallback': !heroVisual.mobile,
                            'lg-fallback': !heroVisual.image,
                        }"
                    />
                </div>
            </div>


            <!-- Content -->
            <div
                class="hero-content"
            >
                <div class="page-container">

                    <div class="hero-grid">
                        <div
                            ref="content"
                            class="hero-cell"
                        >

                            <!-- School -->
                            <div
                                class="hero-school transition-up-1"
                                :class="heroSchool.class"
                            >
                                <div class="school">
                                    <WebpPicture
                                        v-if="heroSchool.image.src"
                                        class="icon"
                                        :class="`${heroSchool.image.class || ''} ${heroSchool.default ? 'default' : ''}`"
                                        :src="heroSchool.image.src"
                                        :alt="heroSchool.image.alt || heroSchool.name"
                                        :width="heroSchool.image.width"
                                        :height="heroSchool.image.height"
                                    />

                                    <span
                                        v-if="heroSchool.name"
                                        class="name"
                                        v-html="heroSchool.name"
                                    />
                                </div>
                            </div>

                            <!-- Title -->
                            <h1
                                class="hero-title fw-800 transition-up-1"
                                :class="heroTitle.class"
                                v-html="heroTitle.text"
                            />

                            <!-- Stats -->
                            <div
                                v-if="isLaunched && (hasReviews || hasStudents)"
                                class="hero-stats transition-up-1"
                            >
                                <!-- Student count -->
                                <div v-if="hasStudents" class="hero-students">
                                    <span class="icon-ic-users mr-h" />
                                    <span>
                                        {{ numberFormat(course.students) }} {{ $tc('stats.students', course.students, locale) }}
                                    </span>
                                </div>

                                <!-- Review score -->
                                <div v-if="hasReviews" class="hero-reviews">
                                    <span class="icon-ic-star color-yellow-2 mr-h" />

                                    <span>
                                        {{ course.rating.average.toLocaleString().split('.').join(',') }}
                                        <span class="color-gray-3">({{ numberFormat(course.rating.length) }})</span>
                                    </span>
                                </div>
                            </div>

                            <!-- About -->
                            <div
                                v-if="heroAbout?.text"
                                class="hero-about transition-up-2"
                                :class="heroAbout.class"
                                v-html="heroAbout.text"
                            />

                            <!-- Actions -->
                            <div class="hero-actions transition-up-3">
                                <div class="actions">

                                    <!-- Link: program -->
                                    <button
                                        v-if="programBlockId"
                                        v-scroll-to="{el: `#${programBlockId}`, offset: linkOffset}"
                                        class="action btn-primary"
                                    >
                                        <span>{{ $t('actions.program', locale) }}</span>
                                    </button>

                                    <!-- Link: preregister -->
                                    <button
                                        v-if="!isLaunched && applyBlockId"
                                        v-scroll-to="{el: `#${applyBlockId}`, offset: linkOffset}"
                                        class="action btn-secondary btn-custom"
                                    >
                                        <span>{{ $t('actions.preregister', locale) }}</span>
                                    </button>

                                    <!-- Panel: trial -->
                                    <PanelButton
                                        v-else-if="isLaunched && !hasAccess && canTrialAccess && hasTrial && !isEnrollClosed"
                                        class="action btn-secondary btn-custom"
                                        name="subscription-trial"
                                    >
                                        <span>{{ $t('actions.trial', locale) }}</span>
                                    </PanelButton>

                                    <!-- Link: buy -->
                                    <button
                                        v-else-if="isLaunched && !hasAccess && buyBlockId && !isEnrollClosed"
                                        v-scroll-to="{el: `#${buyBlockId}`, offset: linkOffset}"
                                        class="action btn-secondary btn-custom"
                                    >{{ $t(`actions.${canBuyAccess ? 'buy' : 'subscribe' }`, locale) }}</button>

                                    <!-- Button: join course -->
                                    <JoinCourse
                                        v-else-if="canJoin"
                                        :courseName="course.name"
                                        :courseId="course.id"
                                        :courseSlug="course.slug"
                                        :courseLocale="locale"
                                        class="action btn-secondary btn-custom"
                                    >
                                        <span>{{ $t('actions.join', locale) }}</span>
                                    </JoinCourse>

                                    <!-- Link: to course -->
                                    <nuxt-link
                                        v-else-if="isLaunched && hasAccess && isJoined"
                                        :to="localePath(`/learning/course/${course.slug}`)"
                                        class="action btn-secondary btn-custom"
                                    >
                                        <span>{{ $t('actions.toCourse', locale) }}</span>
                                    </nuxt-link>

                                </div>

                            </div>

                        </div>
                    </div>

                </div>
            </div>

            <!-- Scroll to learn more -->
            <div class="hero-scroll transition-up-3">
                <span class="icon-anim-scroll" />
            </div>

        </div>

        <!-- Mobile-only about block -->
        <div class="mt-6 mt-md-8 d-lg-none ta-center">
            <div class="page-container">

                <h2 class="h4 h3-sm h2-md mt-0 mb-2">{{ $t('about.title', locale) }}</h2>

                <div
                    class="hero-desc"
                    v-html="heroAbout.text"
                />

            </div>
        </div>
    </div>
</template>


<script>
    let uid = 0;

    import WebpPicture  from '~/components/WebpPicture.vue';
    import numberFormat from '~/utilities/numberFormat.js';

    import { useCourseLandingStore } from '~/stores/courseLanding';

    import blockPropertyObject from '~/mixins/course-landing/blockPropertyObject.js';
    import courseStates        from '~/mixins/course-landing/courseStates.js';

    export default {
        props: {
            block: Object,
            edit:  Boolean,
        },

        components: {
            JoinCourse:  () => import('~/components/creative-practice/JoinCourseButton.vue'),
            PanelButton: () => import('~/components/panels/PanelButton.vue'),
            WebpPicture,
        },

        mixins: [
            blockPropertyObject,
            courseStates,
        ],

        data () {
            uid++;

            return {
                linkOffset: -72,
                contentHeight: 0,

                uid
            }
        },

        head () {
            const isVideo = this.heroVisual.video;

            const fallback   = !this.heroVisual.mobile;
            const lgFallback = !this.heroVisual.image;

            const src   = this.heroVisual.mobile || this.course.thumbnail;
            const smSrc = this.heroVisual.tablet || this.course.thumbnail;
            const lgSrc = this.heroVisual.image || this.course.thumbnail;
            const xlSrc = this.heroVisual.xlImage.src;

            let preload = [];

            const isUrlCDN = (url) => {
                if (!url) return false;
                const regex = /^https:\/\/cdn\.(?:[a-z0-9]+\.)?cases\.media\//;

                return regex.test(url);
            };

            const atSupportsWebp = (src, content) => {
                return `@supports (background-image: -webkit-image-set(url(${src}.webp) 1x)) { ${content} }`;
            };

            const atMedia = (query, content) => {
                return `@media screen and ${mediaQuery(query)} { ${content} }`;
            };

            const mediaQuery = (query) => {
                const arr = Array.isArray(query) ? query : [query];
                const str = arr.join(') and (');

                return `(${str})`;
            };

            const bgImageWebp = (src) => {
                return `background-image: -webkit-image-set(url(${src}.webp) 1x); background-image: image-set(url(${src}.webp) 1x);`;
            };

            const preloadImage = (src, query) => {
                const isCDN = isUrlCDN(src);
                let preload = {
                    rel: 'preload',
                    as:  'image',
                };

                if (isCDN) {
                    preload.href = `${src}.webp`;
                    preload.type = 'image/webp';
                } else {
                    preload.href = src;
                }

                if (query) preload.media = mediaQuery(query);

                return preload;
            };

            const preloadVideo = (src, type, query) => {
                let preload = {
                    rel:  'preload',
                    as:   'video',
                    href: src,
                };

                if (type)  preload.type = type;
                if (query) preload.media = mediaQuery(query);

                return preload;
            };

            const isCDN   = isUrlCDN(src);
            const isSmCDN = isUrlCDN(smSrc);
            const isLgCDN = isUrlCDN(lgSrc);
            const isXlCDN = isUrlCDN(xlSrc);

            let css    = '';
            let cdnCss = '';

            // Mobile image
            css += `.visual { background-image: url(${src}); }`;

            if (isCDN) {
                // Use media query here to prevent mobile webp overwriting desktop image
                cdnCss += atSupportsWebp(src,
                    atMedia('max-width: 991px',
                        `.visual { ${bgImageWebp(src)} }`
                    )
                );

                preload.push(preloadImage(src, 'max-width: 991px'));
            }

            // Tablet image
            if (!isVideo) {
                css += atMedia('min-width: 576px',
                    `.visual { background-image: url(${smSrc}); }`
                );

                let query = ['min-width: 576px'];

                if (isSmCDN) {
                    cdnCss += atSupportsWebp(smSrc,
                        atMedia(query,
                            `.visual { ${bgImageWebp(smSrc)} }`
                        )
                    );
                }

                preload.push(preloadImage(smSrc, query));
            }

            // Desktop image
            if (!isVideo) {
                css += atMedia('min-width: 992px',
                    `.visual { background-image: url(${lgSrc}); }`
                );

                let query = ['min-width: 992px'];

                if (xlSrc && isXlCDN) query.push('max-width: 1359px');

                if (isLgCDN) {
                    cdnCss += atSupportsWebp(lgSrc,
                        atMedia(query,
                            `.visual { ${bgImageWebp(lgSrc)} }`
                        )
                    );
                }

                preload.push(preloadImage(lgSrc, query));
            } else {
                for (const video of heroVisual.video) {
                    preload.push(preloadVideo(video.src, video.type, 'min-width: 992px'));
                }
            }

            // Wide desktop image
            if (!isVideo && !lgFallback && xlSrc) {
                css += atMedia('min-width: 1360px',
                    `.visual { background-image: url(${xlSrc}) }`
                );

                if (isXlCDN) {
                    cdnCss += atSupportsWebp(xlSrc,
                        atMedia('min-width: 1360px',
                            `.visual { ${bgImageWebp(xlSrc)} }`
                        )
                    );
                }

                preload.push(preloadImage(xlSrc, 'min-width: 1360px'));
            }

            let cssText = css + cdnCss;

            return {
                style: [
                    { type: 'text/css', cssText, },
                ],
                link: preload,
            };
        },

        computed: {
            locale () {
                return this.courseLandingStore.locale;
            },

            course () {
                return this.courseLandingStore.course;
            },

            courseLandingStore () {
                return useCourseLandingStore();
            },


            hasReviews () {
                return this.course.rating.length > 0;
            },

            hasStudents () {
                return this.course.students
                    && this.course.students > 0;
            },

            programBlockId () {
                const block = this.course?.blocks?.find(block => {
                    if (block.disabled) return false;
                    const type = block.type.toLowerCase();
                    return type == 'program'
                        || type == 'programgroup'
                        || type == 'programlegacy'
                        || type == 'programtable'
                        || type == 'monthesprogram';
                });

                if (!block) return false;

                // Use first block of type (uid == 1)
                return block.id || `${block.type}-1`;
            },

            applyBlockId () {
                const block = this.course?.blocks?.find(block => {
                    if (block.disabled) return false;;
                    const type = block.type.toLowerCase();
                    return type == 'subscribe'
                        || type == 'apply';
                });

                if (!block) return false;

                // Use first block of type (uid == 1)
                return block.id || `${block.type}-1`;
            },

            buyBlockId () {
                const block = this.course?.blocks?.find(block => {
                    if (block.disabled) return false;
                    const type = block.type.toLowerCase();
                    return type == 'buy'
                        || type == 'subscribe'
                        || type == 'subscribelegacy'
                        || type == 'customprice'
                        || type == 'comebackalive';
                });

                if (!block) return false;

                // Use first block of type (uid == 1)
                return block.id || `${block.type}-1`;
            },

            heroVisual () {
                let obj = Object.assign({
                    dark:  false,
                    color: false,
                    mask:  false,

                    video:  false,
                    image:  false,
                    width:  false,
                    height: false,

                    xlImage: false,

                    mobile: false,
                    tablet: false,

                    btnColor: '#1B8BE0',
                    btnLight: false,
                }, this.blockPropertyObject(this.block.visual, 'image'));

                // Set default fallback colors
                if (!obj.color) obj.color = obj.dark ? '#000' : '#fff';

                if (obj.video && !Array.isArray(obj.video)) {
                    // Convert single video source to array
                    obj.video = [obj.video];
                }

                obj.xlImage = Object.assign({
                    src: null,
                    id:  null,
                }, this.blockPropertyObject(this.block.visual?.xlImage || {}, 'src'));

                let hsl = this.hexToHsl(obj.btnColor);

                if (!hsl) hsl = [205.89, 78.49, 49.22];

                const [h, s, l] = hsl;

                obj.btnHue = h;
                obj.btnSaturation = s;
                obj.btnLightness = l;

                return obj;
            },

            heroTitle () {
                let obj = this.blockPropertyObject(this.block.title, 'text');

                if (!obj.text) obj.text = this.course.name;

                return obj;
            },

            heroAbout () {
                return this.blockPropertyObject(this.block.about, 'text');
            },

            heroSchool () {
                let obj = Object.assign({
                    class: false,
                    name:  false,
                    image: false,
                }, this.blockPropertyObject(this.block.school, 'image'));

                if (!obj.image) {
                    obj.image = '/images/creative-practice/landing-cp-logo.svg';
                    obj.default = true;
                }

                obj.image = this.blockPropertyObject(obj.image, 'src');

                return obj;
            },
        },

        mounted () {
            if (process.browser) {
                this.observe();
            }

            window.addEventListener('resize', this.updateContentHeight);

            this.updateContentHeight();
        },

        methods: {
            updateContentHeight () {
                if (this.$refs.content) {
                    this.contentHeight = this.$refs.content.offsetHeight;
                }
            },


            hexToHsl (color) {
                const rgb = this.hexToRgb(color);

                if (!rgb) return false;

                const hsl = this.rgbToHsl(rgb);

                if (!hsl) return false;

                return hsl;
            },

            hexToRgb (hex) {
                // Extract R G and B from hex code
                const short = /^#?(?<r>[a-f0-9])(?<g>[a-f0-9])(?<b>[a-f0-9])$/i;
                const long  = /^#?(?<r>[a-f0-9]{2})(?<g>[a-f0-9]{2})(?<b>[a-f0-9]{2})$/i;

                let regex = long;

                if (hex.length == 3 || hex.length == 4) regex = short;
                else if (hex.length != 6 && hex.length != 7) return false;

                const match = hex.match(regex);

                if (!match || !match.groups) return false;

                let { r, g, b } = match.groups;

                // Normalize length
                if (r.length == 1) r += r;
                if (g.length == 1) g += g;
                if (b.length == 1) b += b;

                // Convert hex to int
                r = parseInt(r, 16);
                g = parseInt(g, 16);
                b = parseInt(b, 16);

                return [r, g, b];
            },

            rgbToHsl (rgb) {
                let [r, g, b] = rgb;

                // https://gist.github.com/mjackson/5311256
                r /= 255, g /= 255, b /= 255;

                let max = Math.max(r, g, b), min = Math.min(r, g, b);
                let h, s, l = (max + min) / 2;

                if (max == min) {
                    h = s = 0; // achromatic
                } else {
                    let d = max - min;
                    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

                    switch (max) {
                        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                        case g: h = (b - r) / d + 2; break;
                        case b: h = (r - g) / d + 4; break;
                    }

                    h /= 6;
                }

                // Convert to degrees/percentages
                h = Math.round(36000 * h) / 100;
                s = Math.round(10000 * s) / 100;
                l = Math.round(10000 * l) / 100;

                return [ h, s, l ];
            },


            numberFormat (x) {
                return numberFormat(x, this.locale);
            },


            observe () {
                this.observer = new IntersectionObserver((entries) => {
                    const entry = entries.find(e => e.isIntersecting);
                    const visible = entry && entry.intersectionRatio >= 0.25;
                    this.$emit('intersection', {
                        type: 'Hero2',
                        threshold: 0.25,
                        isVisible: visible
                    });
                }, {
                    rootMargin: '0px',
                    threshold: [0, 0.03, 0.1, 0.25, 0.5, 0.75, 1.0]
                });

                const blockId = this.block.id || `${this.block.type}-${this.uid}`;

                const el = document.querySelector(`#${blockId}`);
                if (el) this.observer.observe(el);
            },

            unobserve: function () {
                if (this.observer) this.observer.disconnect();
            },
        },

        destroyed () {
            this.unobserve();

            window.removeEventListener('resize', this.updateContentHeight);
        },
    }
</script>


<style lang="postcss" scoped>
    .hero-block {

        --hero-btn-hue:   205.89;
        --hero-btn-sat:   78.49%;
        --hero-btn-light: 49.22%;

        --hero-cell-height:     200px;
        --hero-scroll-height:   32px;
        --hero-block-padding:   calc(
            var(--hero-scroll-height)
            + var(--hero-content-padding)
        );
        --hero-content-padding: 16px;

        color: #000;

        padding-bottom: calc(
            var(--hero-scroll-height)
            + var(--hero-content-padding)
        );

        display:     flex;
        align-items: stretch;

        height:     calc(100vh - 48px);
        height:     calc(100dvh - 48px);
        min-height: calc(
            var(--hero-cell-height) * 2
            + var(--hero-block-padding)
            + var(--hero-content-padding) * 2
        );
        max-height: 710px;

        position: relative;

        @media screen and (min-width: 576px) {
            --hero-content-padding: 32px;
        }

        @media screen and (min-width: 768px) {
            height:     100vh;
            height:     100dvh;
            max-height: 910px;
        }

        @media screen and (min-width: 992px) {
            --hero-scroll-height: 56px;

            height:     calc(100vh - var(--top-bar-height));
            height:     calc(100dvh - var(--top-bar-height));
            min-height: calc(
                var(--hero-cell-height)
                + var(--hero-block-padding)
                + var(--hero-content-padding)
            );
        }

        @media screen and (min-width: 992px) {
            --hero-scroll-height: 64px;
        }

        &.dark {
            color: #fff;
        }

        :deep(.color-gray-3) {
            color: #878d99;
        }

        :deep(.color-yellow-2) {
            color: #FFD601;
        }

        :deep(.btn-primary) {
            color: #fff;
            font-weight: 600;
            background-color: #0E1014;

            &:hover,
            &.exact-active,
            &.active {
                background-color: #595E69;
            }

            &:active {
                background-color: #0E1014 !important;
            }

            &.disabled,
            &:disabled {
                color: #F5F7FA !important;
                background-color: #A8B0BF !important;

                .color-gray-3 {
                    color: #DDE1EB !important;
                }
            }
        }

        :deep(.b-link) {
            color: #1f92cc;

            &:hover {
                color: #000;
            }
        }

        .groupped-link:hover :deep(.b-link) {
            color: #000;
        }

        &.dark {
            :deep(.color-gray-3) {
                color: #A8B0BF;
            }

            :deep(.color-yellow-2) {
                color: #FFD601;
            }

            :deep(.btn-primary) {
                color: #0E1014;
                background-color: #fff;

                &:hover,
                &.exact-active,
                &.active {
                    background-color: #98a1b4;
                }

                &:active {
                    background-color: #fff !important;
                }

                &.disabled,
                &:disabled {
                    color: #1a1a1d !important;
                    background-color: #4d5461 !important;

                    .color-gray-3 {
                        color: #3d3e4a !important;
                    }
                }
            }

            :deep(.b-link) {
                color: #FFCE0A;

                &:hover {
                    color: #fff;
                }
            }

            .groupped-link:hover :deep(.b-link) {
                color: #fff;
            }
        }
    }

    .hero-visual {
        position: absolute;
        top:      0;
        right:    0;
        bottom:   0;
        left:     0;
        z-index:  0;
        overflow: hidden;

        .visual {
            background-position: 50% 50%;
            background-repeat:   no-repeat;
            background-size:     auto 100%;

            margin:        0;
            border-radius: 0;

            width:     auto;
            height:    auto;
            max-width: none;

            position: absolute;
            top:      0;
            right:    0;
            bottom:   0;
            left:     0;
            z-index:  1;
            overflow: hidden;

            &.fallback,
            &.lg-fallback {
                background-size: cover;
            }
        }

        .visual-video {
            transform: translateX(-50%);

            display:   block;
            width:     auto;
            height:    100%;
            max-width: none;

            position: relative;
            left:     50%;

            ~ .page-container > .visual {
                display: none;
            }
        }

        .mask {
            display: block;
            width:   100%;
            height:  100%;

            &::before {
                content: '';

                background: #fff;
                opacity:    .5;

                position: absolute;
                top:      0;
                right:    0;
                bottom:   0;
                left:     0;
                z-index:  2;

                .hero-block.dark & {
                    background: #000;
                }
            }
        }

        .visual ~ .mask {
            position: absolute;
            top:      0;
            right:    0;
            bottom:   0;
            left:     0;
            z-index:  2;
        }
    }

    .hero-content {
        position: relative;
        padding-top:    var(--hero-content-padding);
        padding-bottom: var(--hero-content-padding);

        display:     flex;
        align-items: stretch;
        width:       100%;

        > .page-container {
            display:     flex;
            align-items: stretch;
        }
    }

    .hero-grid {
        flex: 1 1 100%;

        display:            grid;
        grid-template-rows: 1fr 1fr;
        align-items:        end;

        @media screen and (min-width: 576px) {
            padding: 0 32px;
        }

        @media screen and (min-width: 992px) {
            padding: 0;

            grid-template-columns: 1fr 1fr;
            grid-template-rows:    1fr;
            align-items:           center;
        }
    }

    .hero-cell {
        grid-row-start: 2;

        display:               grid;
        grid-gap:              8px;
        grid-template-columns: 1fr;
        justify-items:         center;

        @media screen and (min-width: 576px) {
            grid-gap: 16px;
        }

        @media screen and (min-width: 992px) {
            grid-row-start: 1;
            justify-items:  start;
        }
    }

    .hero-school {
        .icon,
        .icon :deep(img) {
            display:    block;
            width:      auto;
            height:     auto;
            max-height: 32px;
        }

        .icon.default {
            filter: invert(1);
        }
    }

    .hero-block.dark .icon.default {
        filter: invert(0);
    }

    .hero-title + .hero-stats {
        margin-top: -4px;
    }

    .hero-title {
        font-size:   24px;
        line-height: 1.166667;
        text-align:  center;

        margin: 0;

        @media screen and (min-width: 576px) {
            font-size:   32px;
            line-height: 1.25;
        }

        @media screen and (min-width: 768px) {
            font-size:   40px;
            line-height: 1.1;
        }

        @media screen and (min-width: 992px) {
            font-size:   38px;
            line-height: 1.105263;
            text-align:  left;
        }
    }

    .hero-stats {
        display: none;

        @media screen and (min-width: 576px) {
            font-size:   19px;
            line-height: 1.263158;

            display:        flex;
            flex-flow:       row nowrap;
            justify-content: flex-start;
        }

        > * + * {
            margin-left: 8px;

            &::before {
                content: '•';

                color: var(--color-cases-gray-2);

                display: inline-block;

                margin-right: 8px;

                @media (prefers-color-scheme: dark) {
                    color: rgba(255, 255, 255, .5);
                }

                [data-theme='dark'] & {
                    color: rgba(255, 255, 255, .5);
                }

                [data-theme='light'] & {
                    color: var(--color-cases-gray-2);
                }

                @media screen and (min-width: 992px) {
                    color: #878D99;

                    .hero-block.dark & {
                        color: rgba(255, 255, 255, .5);
                    }
                }
            }
        }
    }

    .hero-students,
    .hero-reviews {
        display:     flex;
        flex-flow:   row nowrap;
        align-items: center;
    }

    .hero-about {
        font-size:   19px;
        line-height: 1.263158;
        text-align:  left;

        display: none;

        @media screen and (min-width: 992px) {
            display:   block;
            max-width: 432px;
        }
    }

    .hero-actions {
        margin-top: 16px;

        @media screen and (min-width: 576px) {
            margin-top: 0;

            justify-self: stretch;
        }
    }

    .actions {
        margin: -8px;

        display:     flex;
        flex-flow:   column nowrap;
        align-items: stretch;

        @media screen and (min-width: 576px) {
            margin: -4px;

            flex-flow:       row wrap;
            align-items:     center;
            justify-content: center;
        }

        @media screen and (min-width: 992px) {
            justify-content: flex-start;
        }
    }

    .action {
        text-transform: uppercase;

        margin:  8px;
        padding: 12px 24px;

        flex:      1 0 auto;
        min-width: 200px;

        @media screen and (min-width: 576px) {
            margin:  4px;
            padding: 16px;

            flex:      0 1 auto;
            min-width: 120px;
        }

        @media screen and (min-width: 992px) {
            flex: 0 0 auto;
        }
    }

    .btn-custom {
        color:       #fff;
        font-weight: 600;

        background: hsl(var(--hero-btn-hue), var(--hero-btn-sat), var(--hero-btn-light));

        &:hover {
            background: hsl(var(--hero-btn-hue), var(--hero-btn-sat), calc(var(--hero-btn-light) + 10%));
        }

        &:active {
            background: hsl(var(--hero-btn-hue), var(--hero-btn-sat), calc(var(--hero-btn-light) - 10%));
        }

        .hero-block.hero-btn-light & {
            color: #000000;
        }
    }

    .hero-scroll {
        margin:  0;
        padding: 0;

        display:         flex;
        flex-flow:       row nowrap;
        justify-content: center;

        position: absolute;
        right:    0;
        bottom:   var(--hero-content-padding);
        left:     0;
        z-index:  2;

        > .icon-ic-mice {
            font-size: 18px;

            margin: -1px 0;
            margin-right: 6px;

            display: block;
        }
    }

    .icon-anim-scroll {
        animation:  icon-anim-touch 2.2s cubic-bezier(0.76, 0, 0.3, 1) infinite;
        background:
            linear-gradient(to bottom, #fff 50%, rgba(0, 0, 0, 0) 50%)
        ;
        background-position: 0 -100%;
        background-size:     100% 200%;

        display: block;
        width:   1px;
        height:  var(--hero-scroll-height);

        position: relative;
    }

    @keyframes icon-anim-touch {
        0% {
            background-position: 0 -100%;
        }
        75% {
            background-position: 0 0;
        }
        100% {
            background-position: 0 100%;
        }
    }

    .edit {
        position: absolute;
        top:      16px;
        right:    16px;
        z-index:  10;
    }

    .hero-desc {
        @media screen and (min-width: 576px) {
            font-size:   19px;
            line-height: 1.263158;
        }
    }
</style>
